import { observer } from 'mobx-react-lite'
import React from 'react'

import Collapse from '@mui/material/Collapse'

import List from '@mui/material/List'
import Stack from '@mui/material/Stack'

import { H3 } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  useEvent,
  useGlobalCopyStore,
  useScreenSize,
  useTrendsStore,
} from '../../common'
import { type DetailedCategoryWithTransactions } from '../../core'
import { CollapseExpandListItem } from '../../common/components/listitems/CollapseExpandListItem'
import CategoriesListItem from './CategoriesListItem'
import { TRENDS_ANALYTICS_METADATA } from '../constants/TrendsWidget'

interface CategoriesListProps {
  availableHeight?: number
  minHeight?: number
  onCategoryClick: (data: DetailedCategoryWithTransactions) => void
}

const CategoriesList: React.FC<CategoriesListProps> = observer(
  ({ availableHeight = 0, onCategoryClick }) => {
    const { collapsedCategories, visibleCategories, setVisibleListLength } = useTrendsStore()
    const { isLargeDesktop, isDesktop } = useScreenSize()
    const { trends: copy } = useGlobalCopyStore()
    const { onEvent } = useEvent()
    const [isExpanded, setIsExpanded] = React.useState(false)

    const isLargeLayout = isDesktop || isLargeDesktop

    React.useEffect(() => {
      // Divide available height by item height (64px), minus 1 for more/less button
      const newVisibleListLength = Math.floor(availableHeight / 64) - 1
      const totalListItems = visibleCategories.length + collapsedCategories.length

      if (newVisibleListLength !== visibleCategories.length) {
        // Always show at least 5 items on large layout, and 5 items on small layout
        // Also ignores when availableHeight < 0 on initial render
        setVisibleListLength(isLargeLayout ? Math.max(newVisibleListLength, 5) : 5)
        setIsExpanded(newVisibleListLength >= totalListItems)
      }
    }, [availableHeight])

    const handleCollapseExpand = () => {
      setIsExpanded(!isExpanded)
      onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_VIEW_MORE, {
        ...TRENDS_ANALYTICS_METADATA,
      })
    }

    const totalCategories = visibleCategories.length + (isExpanded ? collapsedCategories.length : 0)
    return (
      <Stack sx={{ width: '100%' }}>
        <Stack sx={{ pb: 4, pt: 16, px: 16 }}>
          <H3>{copy.categories}</H3>
        </Stack>
        <List>
          {visibleCategories.map((category, index) => (
            <CategoriesListItem
              key={category.guid}
              {...category}
              isLastItem={index === totalCategories - 1}
              onClick={
                category.transactions.length > 0 ? () => onCategoryClick(category) : undefined
              }
              showDivider={true}
            />
          ))}
          <Collapse
            component="li"
            in={isExpanded}
            sx={{ '&.MuiCollapse-root .MuiListItemButton-root': { paddingLeft: 24 } }}
          >
            <List>
              {collapsedCategories.map((category, index) => {
                const adjustedIndex = visibleCategories.length + index
                return (
                  <CategoriesListItem
                    key={category.guid}
                    {...category}
                    isLastItem={adjustedIndex === totalCategories - 1}
                    onClick={
                      category.transactions.length > 0 ? () => onCategoryClick(category) : undefined
                    }
                    showDivider={true}
                  />
                )
              })}
            </List>
          </Collapse>
          {collapsedCategories.length > 0 && (
            <CollapseExpandListItem isExpanded={isExpanded} onClick={handleCollapseExpand} />
          )}
        </List>
      </Stack>
    )
  },
)

export default CategoriesList
